import type React from 'react';
import { useCallback } from 'react';

/**
 * Enter submits the form — the one keystroke people expect from every form on
 * the web, and the one antd drawers and modals lose, because their primary
 * button lives in a footer outside the `<form>` and so never triggers the
 * browser's implicit submission.
 *
 * Spread the result onto the `Form` (or any wrapper around the fields):
 *
 *   const submitOnEnter = useSubmitOnEnter(handleSave, { enabled: !saving });
 *   <Form form={form} {...submitOnEnter}> … </Form>
 *
 * What it deliberately does *not* do is take Enter away from a control that
 * already needs it. Typing a paragraph, picking an option from a dropdown and
 * writing code all mean Enter, and a form that submits mid-sentence is worse
 * than one that never submits at all — so every such control is skipped and the
 * keystroke is left to it (`Shift+Enter` in a text area, likewise).
 */

/**
 * Controls whose own Enter behavior outranks submitting: it either inserts a
 * line (text areas, rich text, code) or confirms a choice inside an overlay
 * (everything antd builds on a dropdown).
 *
 * Add `data-submit-on-enter="false"` to opt a subtree out from the outside.
 */
const OPT_OUT_SELECTOR = [
  '[data-submit-on-enter="false"]',
  '.ant-select',
  '.ant-tree-select',
  '.ant-cascader',
  '.ant-picker',
  '.ant-mentions',
  '.ant-upload',
  '.ant-input-number-handler',
  '.cm-editor',
  '.ProseMirror',
].join(',');

/** Enter already means something else on these — activate, browse, reset. */
const OPT_OUT_INPUT_TYPES = ['submit', 'button', 'reset', 'file'];

/**
 * Whether Enter on this element should be read as "submit the form" rather than
 * as input the focused control wants for itself.
 */
function shouldSubmitFrom(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  // Rich text and code editors are contenteditable, not inputs.
  if (target.isContentEditable) return false;
  // A focused button, link or combobox handles Enter itself.
  if (target.getAttribute('role') === 'combobox') return false;
  if (target.closest(OPT_OUT_SELECTOR)) return false;

  const tagName = target.tagName;
  if (tagName === 'TEXTAREA') return false;
  if (tagName === 'BUTTON' || tagName === 'A' || tagName === 'SELECT') {
    return false;
  }
  // Only ever from a field the user types into — Enter anywhere else in the
  // form (a card, a tab, whatever swallowed the focus) is not a submit intent.
  if (tagName !== 'INPUT') return false;

  const type = (target as HTMLInputElement).type?.toLowerCase();
  return !OPT_OUT_INPUT_TYPES.includes(type ?? '');
}

export interface SubmitOnEnterOptions {
  /**
   * Set to `false` while submitting, or while the form cannot be submitted at
   * all, so a held Enter cannot fire the request twice.
   */
  enabled?: boolean;
}

export interface SubmitOnEnterProps {
  onKeyDown: React.KeyboardEventHandler<HTMLElement>;
}

export function useSubmitOnEnter(
  onSubmit: (() => void) | undefined | null,
  { enabled = true }: SubmitOnEnterOptions = {},
): SubmitOnEnterProps {
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (!enabled || !onSubmit) return;
      if (event.key !== 'Enter') return;
      // Shift+Enter is the "new line, don't submit" convention; the others are
      // shortcuts belonging to the control or the browser.
      if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }
      // Enter while an IME candidate window is open commits the candidate.
      if ((event.nativeEvent as KeyboardEvent).isComposing) return;
      // Already handled — by an inner control, or by a nested form's own
      // handler, since `preventDefault` below is what marks it as taken.
      if (event.defaultPrevented) return;
      if (!shouldSubmitFrom(event.target)) return;

      // Also cancels the browser's implicit submission, so a form that has a
      // submit button inside it cannot end up submitting twice.
      event.preventDefault();
      onSubmit();
    },
    [enabled, onSubmit],
  );

  return { onKeyDown };
}

export default useSubmitOnEnter;
